import type { Campaign, Sale, Snapshot, ViewRow } from './data';

export type Filters = {
  current: string;
  comparison: string;
  from: string;
  to: string;
  cmpFrom: string;
  cmpTo: string;
  refund: 'Before Refund' | 'After Refund';
  currency: string;
  room: string;
  packageType: string;
  booking: string;
  section: string;
  region?: string;
};
export const COLOURS = { current: '#0F464F', comparison: '#8CA9AD' };
export const VIEW_LABELS = ['Deals', 'PCM', 'Vendor'] as const;
export function defaults(s: Snapshot): Filters {
  const c = s.campaigns.find((c) => c.offer_opportunity_name === 'Double Six Luxury Hotel LPC') ?? s.campaigns[0];
  const p = s.campaigns.find((c) => c.offer_opportunity_name === 'Double Six Luxury Hotel LPC');
  return {
    current: c?.offer_bk ?? 'Double Six Luxury Hotel LPC',
    comparison: p?.offer_bk ?? '',
    from: '2026-01-27',
    to: '2026-03-23',
    cmpFrom: '2025-12-02',
    cmpTo: '2026-01-26',
    refund: 'Before Refund',
    currency: 'Vendor Currency',
    room: 'All',
    packageType: 'All',
    booking: 'All',
    section: 'Performance',
  };
}
export const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
export function sum<T>(rows: T[], fn: (r: T) => number | null | undefined): number | null {
  const ns = rows.map(fn).filter(finite);
  return ns.length ? ns.reduce((a, b) => a + b, 0) : null;
}
export const ratio = (a: number | null, b: number | null) => (finite(a) && finite(b) && b !== 0 ? a / b : null);
export const delta = (a: number | null, b: number | null) => (finite(a) && finite(b) && b !== 0 ? (a - b) / b : null);
export function changeLabel(a: number | null, b: number | null, rate = false) {
  const v = rate ? diff(a, b) : delta(a, b);
  return v === null
    ? 'Unknown'
    : rate
      ? new Intl.NumberFormat('en-AU', { maximumFractionDigits: 1 }).format(v * 100) + ' pp'
      : format(v, 'percent');
}
/** Saved scorecards have distinct null/zero rules; do not substitute a generic delta. */
export function scorecardChange(id: keyof Totals, current: number | null, comparison: number | null) {
  if ((id === 'packages' || id === 'revenue') && current === 0) return 'Unknown';
  if (id === 'conversion' && comparison === 0) return 'Unknown';
  if (id === 'margin')
    return finite(current) && finite(comparison) && comparison !== 0
      ? format((current - comparison) / Math.abs(comparison), 'percent')
      : 'Unknown';
  return changeLabel(current, comparison, id === 'conversion' || id === 'upgrade' || id === 'members');
}
export const diff = (a: number | null, b: number | null) => (finite(a) && finite(b) ? a - b : null);
export const days = (a: string, b: string) =>
  Math.round((Date.parse(b + 'T00:00:00Z') - Date.parse(a + 'T00:00:00Z')) / 86400000);
export function dateValid(d: string) {
  return (
    /^\d{4}-\d{2}-\d{2}$/.test(d) &&
    !isNaN(Date.parse(d)) &&
    new Date(d + 'T00:00:00Z').toISOString().slice(0, 10) === d
  );
}
export function errors(s: Snapshot, f: Filters) {
  const es: string[] = [];
  for (const [name, id, from, to] of [
    ['Current', f.current, f.from, f.to],
    ['Comparison', f.comparison, f.cmpFrom, f.cmpTo],
  ]) {
    if (!id && name === 'Comparison') continue;
    if (!s.campaigns.some((c) => c.offer_bk === id)) es.push(name + ' offer is unavailable in this snapshot.');
    if (!dateValid(from) || !dateValid(to) || from > to) es.push(name + ' dates must be valid and in order.');
    else if (from < s.meta.from || to > s.meta.asOf) es.push(name + ' dates are outside the snapshot.');
  }
  if (f.current === f.comparison && f.from <= f.cmpTo && f.cmpFrom <= f.to)
    es.push('The same offer has overlapping current and comparison dates. Choose distinct windows.');
  if (!['Before Refund', 'After Refund'].includes(f.refund)) es.push('Choose a supported refund basis.');
  const currencies = [
    'Vendor Currency',
    'AUD',
    ...new Set(s.sales.map((r) => r.cost_currency).filter((v): v is string => !!v)),
  ];
  if (!currencies.includes(f.currency)) es.push('Currency is unavailable.');
  return es;
}
function distinct(rs: Sale[], field: 'orderKey' | 'itemKey') {
  return new Set(rs.map((r) => r[field]).filter((v) => v !== null)).size;
}
export function totals(rows: Sale[], views: ViewRow[], f: Filters) {
  const sales = rows.filter((r) => r.transaction_side === 'sale'),
    refunds = rows.filter((r) => r.transaction_side === 'refund'),
    base = sales.filter((r) => r.transaction_type !== 'surcharge'),
    refbase = refunds.filter((r) => r.transaction_type !== 'surcharge');
  const val = (r: Sale) => (f.currency === 'AUD' ? r.cost_sales_aud : r.cost_sales);
  const net = (r: Sale) => {
    const a = val(r),
      h = f.currency === 'AUD' ? r.vendor_holdback_amount_aud : r.vendor_holdback_amount;
    return finite(a) && finite(h) ? a - h : null;
  };
  const has = rows.length > 0;
  const count = (a: Sale[], b: Sale[], field: 'orderKey' | 'itemKey') =>
    has ? distinct(a, field) - (f.refund === 'After Refund' ? distinct(b, field) : 0) : null;
  const signedNights = sum(rows, (r) =>
    r.transaction_side === 'sale'
      ? r.number_of_nights
      : f.refund === 'After Refund' && r.transaction_side === 'refund' && finite(r.number_of_nights)
        ? -r.number_of_nights
        : null,
  );
  const packages = count(base, refbase, 'itemKey'),
    orders = count(sales, refunds, 'orderKey'),
    nights = signedNights;
  const gross = sum(sales, val),
    revenue = sum(rows, net),
    viewCount = sum(views, (r) => r.views),
    attributed = sum(views, (r) => r.packages);
  const gm = sum(rows, (r) =>
    finite(r.margin_aud) && finite(r.margin_aud_refund) ? r.margin_aud - r.margin_aud_refund : null,
  );
  const lead = rows.filter((r) => r.check_in_date).map((r) => days(r.date, r.check_in_date!));
  const saving = sales.filter(
    (r) =>
      r.luxplus_offer_member_price_flag === true &&
      (r.luxplus_non_member_price_aud ?? 0) > 0 &&
      (r.luxplus_member_price_aud ?? 0) > 0,
  );
  return {
    packages,
    orders,
    nights,
    revenue,
    gross,
    adr: ratio(
      gross,
      sum(sales, (r) =>
        finite(r.number_of_nights) && finite(r.number_of_rooms) ? r.number_of_nights * r.number_of_rooms : null,
      ),
    ),
    alos: ratio(
      sum(sales, (r) => r.number_of_nights),
      sum(sales, (r) => r.number_of_packages),
    ),
    lead: lead.length ? lead.reduce((a, b) => a + b, 0) / lead.length : null,
    upgrade: base.some((r) => r.leadroom !== null)
      ? ratio(
          distinct(
            base.filter((r) => r.leadroom === false),
            'itemKey',
          ),
          distinct(base, 'itemKey'),
        )
      : null,
    inclusions: ratio(
      rows.filter((r) => r.inclusion_upgrade_flag).length,
      rows.filter((r) => r.itemKey !== null).length,
    ),
    members: has
      ? ratio(
          distinct(
            sales.filter((r) => r.luxplus_member_flag),
            'orderKey',
          ),
          distinct(sales, 'orderKey'),
        )
      : null,
    newMembers: has
      ? distinct(
          sales.filter((r) => r.luxplus_order_contains_membership_flag),
          'orderKey',
        ) -
        distinct(
          refunds.filter((r) => r.luxplus_order_contains_membership_flag),
          'orderKey',
        )
      : null,
    repeat: has
      ? distinct(
          sales.filter((r) => r.first_sale_flag === false),
          'orderKey',
        )
      : null,
    savings: saving.length
      ? sum(saving, (r) => r.luxplus_non_member_price_aud! - r.luxplus_member_price_aud!)! / saving.length
      : null,
    views: viewCount,
    conversion: ratio(attributed, viewCount),
    margin: gm,
    mpv: ratio(
      sum(views, (r) => r.marginBefore),
      viewCount,
    ),
    attributed,
    sourceRows: rows.length,
    viewRows: views.length,
  };
}
export type Totals = ReturnType<typeof totals>;
export type Metric = { id: keyof Totals; label: string; unit: 'number' | 'money' | 'percent' | 'days'; note: string };
export const vendorMetrics: Metric[] = [
  {
    id: 'revenue',
    label: 'Net vendor revenue',
    unit: 'money',
    note: 'Campaign-window sum of cost sales less vendor holdback, independent of refund selector (source revision 4.2).',
  },
  {
    id: 'packages',
    label: 'Packages sold',
    unit: 'number',
    note: 'Distinct non-surcharge items; subtract distinct refunded items on After Refund.',
  },
  {
    id: 'orders',
    label: 'Bookings',
    unit: 'number',
    note: 'Distinct orders; subtract distinct refunded orders on After Refund.',
  },
  { id: 'nights', label: 'Room nights sold', unit: 'number', note: 'Signed nights, no extra surcharge exclusion.' },
  {
    id: 'adr',
    label: 'Average daily rate',
    unit: 'money',
    note: 'Sale-side cost / sum(nights × rooms), independent of refund selector.',
  },
  {
    id: 'alos',
    label: 'Average length of stay',
    unit: 'days',
    note: 'Sale-side nights / sale-side number of packages, all transaction types in the saved scorecard.',
  },
  {
    id: 'lead',
    label: 'Average lead time',
    unit: 'days',
    note: 'Average check-in date minus transaction date across scoped records.',
  },
  {
    id: 'upgrade',
    label: 'Room upgrades',
    unit: 'percent',
    note: 'Distinct sale non-surcharge items with leadroom=false / all sale non-surcharge items.',
  },
  {
    id: 'inclusions',
    label: 'Inclusion upgrades',
    unit: 'percent',
    note: 'Flagged rows / count of non-null item keys; not distinct.',
  },
];
export const pcmMetrics: Metric[] = [
  {
    id: 'views',
    label: 'Offer views',
    unit: 'number',
    note: 'SUM(view_count) from views_with_sales, separate from the benchmark event source.',
  },
  {
    id: 'conversion',
    label: 'Attributed conversion',
    unit: 'percent',
    note: 'SUM(packages) / SUM(view_count) in views_with_sales; not all sales packages / views.',
  },
  {
    id: 'margin',
    label: 'Gross margin after refunds',
    unit: 'money',
    note: 'SUM(margin_aud − margin_aud_refund) from sales_with_refunds, in AUD.',
  },
  {
    id: 'mpv',
    label: 'Margin per view before refunds',
    unit: 'money',
    note: 'SUM(margin_aud) / SUM(view_count) from views_with_sales.',
  },
  {
    id: 'members',
    label: 'LuxPlus member bookings',
    unit: 'percent',
    note: 'Distinct sale member orders / distinct sale orders.',
  },
  {
    id: 'newMembers',
    label: 'New memberships, net',
    unit: 'number',
    note: 'Distinct sale membership orders minus distinct refund membership orders.',
  },
  {
    id: 'repeat',
    label: 'Repeat bookings',
    unit: 'number',
    note: 'Distinct sale orders with first_sale_flag=false; this is a count.',
  },
  {
    id: 'savings',
    label: 'Average LuxPlus savings',
    unit: 'money',
    note: 'Sale-side average positive non-member AUD price less member AUD price.',
  },
  { id: 'upgrade', label: 'Room upgrades', unit: 'percent', note: 'Sale-side non-surcharge item upgrade rate.' },
];
export function channelGroup(channel: string | null) {
  if (['App Push', 'Direct', 'Email', 'GHA'].includes(channel || '')) return channel!;
  if (['Organic Search', 'Organic Social'].includes(channel || '')) return 'Organic';
  if (
    ['Influencers', 'Paid Display', 'Paid Search', 'Paid Search (Brand)', 'Paid Social', 'Paid Video'].includes(
      channel || '',
    )
  )
    return 'Paid Media';
  return 'Other';
}

export function build(s: Snapshot, f: Filters, withCurve = true) {
  const invalid = errors(s, f);
  function side(id: string, from: string, to: string) {
    const raw = s.sales.filter((r) => r.offer_bk === id && r.date >= from && r.date <= to);
    // Currency chooses a real vendor-currency subset; AUD includes every currency after source conversion.
    const rows = invalid.length
      ? []
      : raw.filter(
          (r) =>
            (f.currency === 'AUD' || f.currency === 'Vendor Currency' || r.cost_currency === f.currency) &&
            (f.room === 'All' || (r.room_type_name ?? 'Unknown') === f.room) &&
            (f.packageType === 'All' ||
              (['1st upgrade', '2nd upgrade', '3rd upgrade', '4th+ upgrade'].includes(
                (r.upgrade_level ?? '').toLowerCase().trim(),
              )
                ? 'Indulgence'
                : 'Base') === f.packageType),
        );
    const viewRows =
      invalid.length || f.room !== 'All' || f.packageType !== 'All'
        ? []
        : s.views.filter((r) => r.offer_bk === id && r.date >= from && r.date <= to);
    return { rows, viewRows, totals: totals(rows, viewRows, f) };
  }
  const current = side(f.current, f.from, f.to),
    comparison = side(f.comparison, f.cmpFrom, f.cmpTo);
  function groups(key: (r: Sale) => string) {
    const names = [...new Set([...current.rows, ...comparison.rows].map(key))];
    return names
      .map((name) => ({
        name,
        current: totals(
          current.rows.filter((r) => key(r) === name),
          [],
          f,
        ),
        comparison: totals(
          comparison.rows.filter((r) => key(r) === name),
          [],
          f,
        ),
      }))
      .sort((a, b) => (b.current.packages ?? -Infinity) - (a.current.packages ?? -Infinity));
  }
  const rooms = groups((r) => r.room_type_name ?? 'Unknown'),
    customers = groups((r) => r.country_name ?? 'Unknown');
  const stayRows = (rows: Sale[]) =>
    rows.filter(
      (r) =>
        f.booking === 'All' ||
        (r.reservation_type_name === 'Buy Now Book later'
          ? 'BNBL Bookings'
          : r.reservation_type_name === 'Instant Booking' &&
              r.check_in_date &&
              r.check_in_date < new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney' }).format(new Date())
            ? 'Bookings Before Today'
            : 'Upcoming Bookings Only') === f.booking,
    );
  const stayNames = [
    ...new Set(
      [...stayRows(current.rows), ...stayRows(comparison.rows)].map((r) => r.check_in_date?.slice(0, 7) ?? 'Unknown'),
    ),
  ].sort();
  const stays = stayNames.map((name) => ({
    name,
    current: totals(
      stayRows(current.rows).filter((r) => (r.check_in_date?.slice(0, 7) ?? 'Unknown') === name),
      [],
      { ...f, refund: 'After Refund' },
    ),
    comparison: totals(
      stayRows(comparison.rows).filter((r) => (r.check_in_date?.slice(0, 7) ?? 'Unknown') === name),
      [],
      { ...f, refund: 'After Refund' },
    ),
  }));
  const weekdays = groups((r) =>
    r.check_in_date
      ? new Intl.DateTimeFormat('en-GB', { weekday: 'long', timeZone: 'UTC' }).format(
          new Date(r.check_in_date + 'T00:00:00Z'),
        )
      : 'Unknown',
  );
  weekdays.sort(
    (a, b) =>
      ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday', 'Unknown'].indexOf(a.name) -
      ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday', 'Unknown'].indexOf(b.name),
  );
  const leads = groups((r) => {
    if (!r.check_in_date || !r.order_date) return 'Unknown';
    const lead = days(r.order_date, r.check_in_date);
    if (lead < 0) return 'Unknown';
    if (lead <= 30) return '0–30';
    if (lead > 300) return '301+';
    const upper = Math.ceil(lead / 30) * 30;
    return `${upper - 29}–${upper}`;
  });
  leads.sort(
    (a, b) =>
      (a.name === 'Unknown' ? Infinity : parseInt(a.name, 10)) -
      (b.name === 'Unknown' ? Infinity : parseInt(b.name, 10)),
  );
  const packageMix = groups((r) =>
    ['1st upgrade', '2nd upgrade', '3rd upgrade', '4th+ upgrade'].includes((r.upgrade_level || '').toLowerCase().trim())
      ? 'Indulgence'
      : 'Base',
  );
  function parityFor(opportunity: string, from: string, to: string) {
    const rows = (s.parity || []).filter(
      (r) => r.offer_opportunity_name === opportunity && r.report_date >= from && r.report_date <= to,
    );
    const coverage = new Set(rows.filter((r) => (r.total_impressions || 0) > 0).map((r) => r.report_date)).size;
    return {
      priceDiff: ratio(
        sum(rows, (r) => r.sum_price_diff),
        sum(rows, (r) => r.sum_le_price),
      ),
      coverage,
      totalDays: days(from, to) + 1,
      rows,
    };
  }
  const parity = {
    current: parityFor(f.current, f.from, f.to),
    comparison: parityFor(f.comparison, f.cmpFrom, f.cmpTo),
  };
  function regional(opportunity: string, from: string, to: string) {
    const field =
      f.region === 'Country'
        ? 'country_match'
        : f.region === 'Area Level 1'
          ? 'area1_match'
          : f.region === 'Area Level 2'
            ? 'area2_match'
            : 'territory_match';
    const rows = (s.regionSales || []).filter((r) => r[field] && r.date >= from && r.date <= to);
    const dates = [...new Set(rows.map((r) => r.date))].sort();
    return dates.map((date, index) => {
      // Tableau WINDOW_SUM(-6, 0) is the previous six visible date marks, not a calendar interval.
      const window = dates.slice(Math.max(0, index - 6), index + 1);
      const subset = rows.filter((r) => window.includes(r.date));
      return {
        day: days(from, date),
        value: ratio(
          sum(
            subset.filter((r) => r.offer_opportunity_name === opportunity),
            (r) => r.booking_weight,
          ) ?? 0,
          sum(subset, (r) => r.booking_weight),
        ),
      };
    });
  }
  const regionalCurrent = regional(f.current, f.from, f.to),
    regionalComparison = regional(f.comparison, f.cmpFrom, f.cmpTo);
  const regionalCurve = [...new Set([...regionalCurrent, ...regionalComparison].map((r) => r.day))]
    .sort((a, b) => a - b)
    .map((day) => ({
      day,
      current: regionalCurrent.find((r) => r.day === day)?.value ?? null,
      comparison: regionalComparison.find((r) => r.day === day)?.value ?? null,
    }));
  function regionalViews(opportunity: string, from: string, to: string) {
    const field =
      f.region === 'Country' ? 'country_match' : f.region === 'Area Level 1' ? 'area1_match' : 'area2_match';
    const rows = (s.regionViews || []).filter((r) => r[field] && r.date >= from && r.date <= to);
    const dates = [...new Set(rows.map((r) => r.date))].sort();
    return dates.map((date, index) => {
      const window = dates.slice(Math.max(0, index - 6), index + 1);
      const subset = rows.filter((r) => window.includes(r.date));
      return {
        day: days(from, date),
        value: ratio(
          sum(
            subset.filter((r) => r.offer_opportunity_name === opportunity),
            (r) => r.unique_views,
          ) ?? 0,
          sum(subset, (r) => r.unique_views),
        ),
      };
    });
  }
  const viewsCurrent = regionalViews(f.current, f.from, f.to),
    viewsComparison = regionalViews(f.comparison, f.cmpFrom, f.cmpTo);
  const regionalViewCurve = [...new Set([...viewsCurrent, ...viewsComparison].map((r) => r.day))]
    .sort((a, b) => a - b)
    .map((day) => ({
      day,
      current: viewsCurrent.find((r) => r.day === day)?.value ?? null,
      comparison: viewsComparison.find((r) => r.day === day)?.value ?? null,
    }));
  const priceDates = [...new Set(parity.current.rows.map((r) => r.report_date))].sort();
  const priceCurve = priceDates.map((date) => {
    const rows = parity.current.rows.filter((r) => r.report_date === date);
    return {
      day: days(f.from, date),
      priceDiff: ratio(
        sum(rows, (r) => r.sum_price_diff),
        sum(rows, (r) => r.sum_le_price),
      ),
      bookings: sum(rows, (r) => r.bookings),
    };
  });
  const channels = [
    ...new Set([...current.viewRows, ...comparison.viewRows].map((r) => channelGroup(r.traffic_channel))),
  ]
    .map((name) => ({
      name,
      current: sum(
        current.viewRows.filter((r) => channelGroup(r.traffic_channel) === name),
        (r) => r.views,
      ),
      comparison: sum(
        comparison.viewRows.filter((r) => channelGroup(r.traffic_channel) === name),
        (r) => r.views,
      ),
    }))
    .sort((a, b) => (b.current ?? 0) - (a.current ?? 0));
  function curve(rows: Sale[], views: ViewRow[], start: string) {
    const ds = [...new Set([...rows.map((r) => r.date), ...views.map((r) => r.date)])].sort();
    return ds.map((date) => ({
      day: days(start, date),
      date,
      nights: totals(
        rows.filter((r) => r.date <= date),
        [],
        f,
      ).nights,
      views: sum(
        views.filter((r) => r.date <= date),
        (r) => r.views,
      ),
    }));
  }
  const a = withCurve ? curve(current.rows, current.viewRows, f.from) : [],
    b = withCurve ? curve(comparison.rows, comparison.viewRows, f.cmpFrom) : [];
  const curveRows = [...new Set([...a, ...b].map((r) => r.day))]
    .sort((a, b) => a - b)
    .map((day) => {
      const c = a.find((r) => r.day === day),
        p = b.find((r) => r.day === day);
      return {
        day,
        current: c?.nights ?? null,
        comparison: p?.nights ?? null,
        views: a.filter((r) => r.day <= day).slice(-1)[0]?.views ?? null,
        cmpViews: b.filter((r) => r.day <= day).slice(-1)[0]?.views ?? null,
      };
    });
  const roomDates = [...new Set(current.rows.map((r) => r.date))].sort();
  const roomCurves = rooms.map((room) => ({
    name: room.name,
    values: roomDates.map((date) => ({
      day: days(f.from, date),
      nights: totals(
        current.rows.filter((r) => (r.room_type_name ?? 'Unknown') === room.name && r.date <= date),
        [],
        f,
      ).nights,
    })),
  }));
  return {
    invalid,
    current,
    comparison,
    rooms,
    stays,
    customers,
    channels,
    weekdays,
    leads,
    packageMix,
    parity,
    regionalCurve,
    regionalViewCurve,
    roomCurves,
    priceCurve,
    curve: curveRows,
    currentCampaign: s.campaigns.find((c) => c.offer_bk === f.current),
    comparisonCampaign: s.campaigns.find((c) => c.offer_bk === f.comparison),
    viewScopeUnknown: f.room !== 'All' || f.packageType !== 'All',
    currencyScopeDifferent: f.currency !== 'AUD',
    roomsAvailable: [
      ...new Set(
        s.sales
          .filter((r) => r.offer_bk === f.current || r.offer_bk === f.comparison)
          .map((r) => r.room_type_name ?? 'Unknown'),
      ),
    ].sort(),
  };
}
export type Model = ReturnType<typeof build>;
export function format(v: number | null | undefined, unit: string = 'number', currency = 'AUD') {
  if (!finite(v)) return 'Unknown';
  return (
    new Intl.NumberFormat('en-AU', {
      style: unit === 'percent' ? 'percent' : 'decimal',
      maximumFractionDigits:
        unit === 'percent' ? 1 : unit === 'days' || unit === 'money' || unit === 'moneyValue' ? 2 : 0,
    }).format(v) + (unit === 'days' ? ' days' : unit === 'money' ? ' ' + (currency === 'AUD' ? 'A$' : currency) : '')
  );
}
export function selectCampaign(s: Snapshot, f: Filters, id: string, comparison = false): Filters {
  const c = s.campaigns.find((c) => c.offer_bk === id);
  if (!c) return { ...f, comparison: '' };
  const end = c.end_date < s.meta.asOf ? c.end_date : s.meta.asOf;
  return comparison
    ? { ...f, comparison: id, cmpFrom: c.start_date, cmpTo: end, room: 'All' }
    : { ...f, current: id, from: '2026-01-27', to: end, room: 'All' };
}
export function campaignLabel(c: Campaign | undefined) {
  return c?.offer_opportunity_name ?? 'No comparison selected';
}
